/**
 * /teaching-plans/:filename — view + edit + delete a single plan.
 *
 * Plans live as markdown files in /documents/teaching-plans/. The
 * default mode is read-only (pre-styled <pre>); click Edit to open
 * a textarea, click Save to re-upload, Delete to wipe and bounce
 * back to the list. The markdown isn't rendered as HTML — teachers
 * see the same source they're editing, which avoids a markdown
 * renderer dep and matches the chat-attachment view.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  PLANS_PATH,
  deleteDoc,
  readDocText,
  uploadDoc,
} from "./documentsApi";
import { parsePlanFilename } from "./serializeMarkdown";

export function TeachingPlanEditPage(): JSX.Element {
  const { t } = useTranslation();
  const { filename } = useParams<{ filename: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const path = filename ? `${PLANS_PATH}/${filename}` : null;

  const body = useQuery({
    queryKey: ["doc-text", path],
    queryFn: () => readDocText(path!),
    enabled: !!path,
    retry: false,
    staleTime: 60_000,
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (body.data !== undefined) setDraft(body.data);
  }, [body.data]);

  const meta = filename ? parsePlanFilename(filename) : { week: null, topicSlug: "" };

  async function save() {
    if (!filename) return;
    setError(null);
    setBusy(true);
    try {
      await uploadDoc(
        PLANS_PATH,
        filename,
        new Blob([draft], { type: "text/markdown" }),
      );
      void qc.invalidateQueries({ queryKey: ["doc-text", path] });
      void qc.invalidateQueries({ queryKey: ["documents-tree", PLANS_PATH] });
      void qc.invalidateQueries({ queryKey: ["documents-tree"] });
      setEditing(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function destroy() {
    if (!filename || !path) return;
    if (!window.confirm(t("teachingPlans.edit.confirmDelete", { name: filename })))
      return;
    setError(null);
    setBusy(true);
    try {
      await deleteDoc(path);
      void qc.invalidateQueries({ queryKey: ["documents-tree", PLANS_PATH] });
      void qc.invalidateQueries({ queryKey: ["documents-tree"] });
      navigate("/teaching-plans");
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <Link to="/teaching-plans" className="text-sm text-sky-600 hover:underline">
            {t("teachingPlans.edit.back")}
          </Link>
          <h1 className="mt-2 text-xl font-semibold text-slate-800">
            {meta.week !== null
              ? t("teachingPlans.edit.titleWithWeek", {
                  week: meta.week,
                  topic: meta.topicSlug.replace(/-/g, " "),
                })
              : filename}
          </h1>
          <p className="text-xs text-slate-500 mt-1 font-mono">{filename}</p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="px-3 py-1 text-xs rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
              >
                {busy ? t("teachingPlans.edit.saving") : t("teachingPlans.edit.save")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setDraft(body.data ?? "");
                }}
                className="px-3 py-1 text-xs rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                {t("teachingPlans.edit.cancel")}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="px-3 py-1 text-xs rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                {t("teachingPlans.edit.edit")}
              </button>
              <button
                type="button"
                onClick={destroy}
                disabled={busy}
                className="px-3 py-1 text-xs rounded text-rose-600 hover:bg-rose-50 disabled:opacity-50"
              >
                {t("teachingPlans.edit.delete")}
              </button>
            </>
          )}
        </div>
      </header>

      {body.isLoading && (
        <p className="text-sm text-slate-500 italic">{t("common.loading")}</p>
      )}
      {body.error && (
        <p className="text-sm text-rose-600">
          {(body.error as Error).message}
        </p>
      )}
      {error && <p className="text-sm text-rose-600 mb-2">{error}</p>}

      {body.data !== undefined &&
        (editing ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={30}
            className="w-full px-3 py-2 rounded border border-slate-200 font-mono text-xs resize-y"
          />
        ) : (
          <div className="teaching-plan-rendered rounded border border-slate-200 bg-white p-6 text-sm text-slate-800 overflow-x-auto">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {body.data}
            </ReactMarkdown>
          </div>
        ))}
    </div>
  );
}
